import { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import BackMethods from "../../back_component/BackMethods";

function ModalHeaderPro({ title, onHide }) {
  return (
    <div className="users-admin-modal__header">
      <div className="users-admin-modal__header-main">
        <h5 className="users-admin-modal__title mb-0">{title}</h5>
      </div>
      <button type="button" className="btn-close" onClick={onHide} aria-label="إغلاق" />
    </div>
  );
}

function InlineField({ label, htmlFor, children }) {
  return (
    <div className="users-admin-modal__inline-field">
      <Form.Label htmlFor={htmlFor} className="users-admin-modal__inline-label">
        {label}
      </Form.Label>
      <div className="users-admin-modal__inline-control">{children}</div>
    </div>
  );
}

function CreateActivityModal({ show, onHide, onSaved }) {
  const { backEnd_request } = BackMethods();
  const [activityType, setActivityType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!show) return;
    setActivityType("");
    setFormError("");
    setIsSubmitting(false);
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!activityType.trim()) {
      setFormError("يرجى إدخال اسم النشاط.");
      return;
    }
    setIsSubmitting(true);
    try {
      await backEnd_request.post("/Activities", { activityType });
      onSaved?.({ message: "تم إضافة النشاط بنجاح ✅", variant: "success" });
      onHide();
    } catch (err) {
      setFormError(err.response?.data?.message || "حدث خطأ أثناء الإضافة");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="users-admin-modal-dialog"
      className="users-admin-modal users-admin-modal--compact"
      dir="rtl"
      backdrop="static"
    >
      <ModalHeaderPro title="إضافة نشاط" onHide={onHide} />
      <Modal.Body>
        <p className="users-admin-modal__hint">
          <i className="bi bi-plus-circle" />
          <span>أدخل اسم النشاط كما سيظهر في النظام للمنشآت والتقارير.</span>
        </p>
        <Form onSubmit={handleSubmit}>
          <Row className="g-2 gx-lg-3">
            <Col lg={12}>
              <InlineField label="اسم النشاط" htmlFor="activity-type">
                <Form.Control
                  id="activity-type"
                  size="sm"
                  className="rounded-2"
                  placeholder="اكتب اسم النشاط..."
                  value={activityType}
                  onChange={(e) => setActivityType(e.target.value)}
                />
              </InlineField>
            </Col>
          </Row>
          {formError ? (
            <div className="alert alert-danger py-1 px-2 small mb-0 mt-2" role="alert">
              {formError}
            </div>
          ) : null}
          <div className="users-admin-modal__footer d-flex gap-2 justify-content-end mt-3">
            <Button variant="outline-secondary" size="sm" type="button" onClick={onHide} disabled={isSubmitting}>
              إلغاء
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="ms-1" /> إضافة
                </>
              ) : (
                "إضافة النشاط"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

function EditActivityModal({ show, activityId, onHide, onSaved }) {
  const { backEnd_request } = BackMethods();
  const [activity, setActivity] = useState({
    activityType: "",
    isActive: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!show || !activityId) return;
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        setFormError("");
        const res = await backEnd_request.get(`/Activities/${activityId}`);
        if (cancelled) return;
        const data = res.data || res || {};
        setActivity({
          activityType: data.activityType ?? "",
          isActive: data.isActive ?? true,
        });
      } catch (err) {
        if (!cancelled) {
          setFormError(err.response?.data?.message || "فشل جلب بيانات النشاط");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, activityId, backEnd_request]);

  const handleChange = (field, value) => {
    setActivity((prev) => ({
      ...prev,
      [field]: field === "isActive" ? Boolean(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!activity.activityType.trim()) {
      setFormError("يرجى إدخال اسم النشاط.");
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        activityType: activity.activityType,
        isActive: activity.isActive,
      };
      await backEnd_request.post(`/Activities/${activityId}`, payload);
      onSaved?.({ message: "تم تعديل النشاط بنجاح ✅", variant: "success" });
      onHide();
    } catch (err) {
      setFormError(err.response?.data?.message || "تعذر حفظ التعديلات");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="users-admin-modal-dialog"
      className="users-admin-modal users-admin-modal--compact"
      dir="rtl"
      backdrop="static"
    >
      <ModalHeaderPro title="تعديل نشاط" onHide={onHide} />
      <Modal.Body>
        {formError ? (
          <div className="alert alert-danger py-1 px-2 small mb-2" role="alert">
            {formError}
          </div>
        ) : null}
        <Form onSubmit={handleSubmit}>
          <Row className="g-2 gx-lg-3">
            <Col lg={6}>
              <InlineField label="اسم النشاط" htmlFor="edit-activity-type">
                <Form.Control
                  id="edit-activity-type"
                  size="sm"
                  className="rounded-2"
                  value={activity.activityType}
                  onChange={(e) => handleChange("activityType", e.target.value)}
                  disabled={isLoading}
                />
              </InlineField>
            </Col>
            <Col lg={6}>
              <InlineField label="الحالة" htmlFor="edit-activity-active">
                <Form.Check
                  type="switch"
                  id="edit-activity-active"
                  label={activity.isActive ? "نشط" : "غير نشط"}
                  checked={activity.isActive}
                  onChange={(e) => handleChange("isActive", e.target.checked)}
                  disabled={isLoading}
                />
              </InlineField>
            </Col>
          </Row>
          <div className="users-admin-modal__footer d-flex gap-2 justify-content-end mt-3">
            <Button variant="outline-secondary" size="sm" type="button" onClick={onHide} disabled={isSubmitting}>
              إلغاء
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={isSubmitting || isLoading}>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="ms-1" /> حفظ
                </>
              ) : (
                "حفظ التغييرات"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default function ActivitiesManagementModals({
  modal,
  activityId,
  onClose,
  onSaved,
  canCreate = true,
  canEdit = true,
}) {
  return (
    <>
      <CreateActivityModal show={canCreate && modal === "create"} onHide={onClose} onSaved={onSaved} />
      <EditActivityModal show={canEdit && modal === "edit"} activityId={activityId} onHide={onClose} onSaved={onSaved} />
    </>
  );
}

